import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useContext,
  forwardRef,
  useImperativeHandle
} from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import InView from './InView';
import * as Styled from './Carousel.styled';
import { IcnChevron } from '../BombasIcons';
import { ContainerQueryContext } from '../../util/ContainerQuery.context';

const Carousel = forwardRef(
  (
    {
      children,
      visibleSlidesCount,
      numSlides,
      showDots,
      withPeeking,
      withPadding,
      withMargin,
      withArrowButtonVerticalOffset,
      controls = 'native',
      isInSmallContainer = false,
      onPositionChange = () => {},
      ...rest
    },
    ref
  ) => {
    const CarouselRef = useRef(null);
    const [carouselRefHasLoaded, setCarouselRefHasLoaded] = useState(false);
    const [onDrag, setOnDrag] = useState({});
    const [dragDirection, setDragDirection] = useState(null);
    const [currentSlidesInView, setCurrentSlidesInView] = useState([0]);
    /**
     * Due to the carousel's `mandatory` snap rule, it will sometimes load with the second, third, (etc) slide showing first
     *
     * This prevents a snap rule from being applied until MouseEnter or TouchStart, so the carousel can initially load with the first slide showing
     */
    const [hasSnappingRule, setHasSnappingRule] = useState(false);
    const { styleQueryMode } = useContext(ContainerQueryContext);

    /**
     * When the user mouses down on the carousel
     * collect data for mouse move to enable scrolling
     *
     * @param {SyntheticBaseEvent} e - Observable event
     * @listens onMouseDown
     */
    const onMouseDown = (e) => {
      setOnDrag({
        initialMouseXPosition: e.clientX,
        initialScrollPosition: e.currentTarget.scrollLeft
      });
    };
    /**
     * While user is dragging and moving the mouse over the carousel,
     * make it scroll
     *
     * @param {SyntheticBaseEvent} e - Observable event
     * @listens onMouseMove
     */
    const onMouseMove = (e) => {
      e.preventDefault();

      const { initialMouseXPosition, initialScrollPosition } = onDrag;

      if (initialMouseXPosition) {
        setOnDrag((state) => ({
          ...state,
          isDragging: true
        }));

        if (initialMouseXPosition - e.clientX > 0) setDragDirection('next');
        else setDragDirection('previous');

        CarouselRef.current.scrollTo({
          left: initialScrollPosition + (initialMouseXPosition - e.clientX)
        });
      }
    };
    const resetDragState = useCallback(() => setOnDrag({}), []);

    const getSlideWidth = () => {
      if (!CarouselRef.current?.children.length) return 0;
      const slides = CarouselRef.current.children;
      return slides[0].offsetWidth;
    };

    /**
     * Scrolls the carousel by one card either left or right
     *
     * @param {HTMLElement} CarouselNode - DOM Node
     * @param {SyntheticBaseEvent|null} e - Observable event or null when called imperatively
     * @param {string|null} imperativeDirection - Direction when called imperatively
     * @listens onClick
     */
    const onSlideChange = (CarouselNode, e, imperativeDirection = null) => {
      const slideWidth = getSlideWidth();
      const currentScrollPosition = CarouselNode.scrollLeft;
      const direction =
        imperativeDirection || (e && e.currentTarget.getAttribute('direction'));
      const sign = direction === 'next' ? 1 : -1;

      CarouselNode.scrollTo({
        left: currentScrollPosition + sign * slideWidth,
        behavior: 'smooth'
      });
    };

    const calculateCurrentVisualSlide = useCallback(() => {
      const node = CarouselRef.current;

      if (!node?.children.length) return 1;
      const containerCenter = node.scrollLeft + node.clientWidth / 2;
      const slides = Array.from(node.children);

      const distances = slides.map((slide) => {
        const center = slide.offsetLeft + slide.offsetWidth / 2;
        return Math.abs(center - containerCenter);
      });

      const best = Math.min(...distances);
      const bestIndex = distances.indexOf(best);

      return bestIndex + 1;
    }, [children, currentSlidesInView, getSlideWidth, visibleSlidesCount]);

    useImperativeHandle(ref, () => ({
      slideNext: () => {
        if (!carouselRefHasLoaded) return;
        onSlideChange(CarouselRef.current, null, 'next');
      },
      slidePrev: () => {
        if (!carouselRefHasLoaded) return;
        onSlideChange(CarouselRef.current, null, 'previous');
      }
    }));

    const handleInViewChange = (slide, isInView) => {
      setCurrentSlidesInView((state) => {
        if (!isInView) {
          return state.filter((item) => item !== slide).sort((a, b) => a - b);
        }
        return [...new Set([...state, slide])].sort((a, b) => a - b);
      });
    };

    /**
     * If the carousel is re rendered with new children, reset to the first slide
     */
    useEffect(() => {
      if (carouselRefHasLoaded) {
        CarouselRef.current.scrollTo({ left: 0 });
      }

      setCurrentSlidesInView([0]);
    }, [React.Children.count(children)]);

    useEffect(
      () => setCarouselRefHasLoaded(!!CarouselRef.current),
      [CarouselRef.current]
    );

    useEffect(() => {
      if (!onDrag.isDragging && carouselRefHasLoaded) {
        const sign = dragDirection === 'previous' ? -1 : 1;

        CarouselRef.current.scrollBy({
          left: sign,
          behavior: 'smooth'
        });
      }
    }, [onDrag.isDragging]);

    useEffect(() => {
      // We only want to update the position when the carousel is controlled by external controls
      if (controls !== 'external') return;

      const node = CarouselRef.current;
      if (!node) return;

      const handleScrollPosition = debounce(() => {
        if (!isInSmallContainer) {
          onPositionChange(currentSlidesInView[0] + 1);
        } else {
          const smallContainerSlide = calculateCurrentVisualSlide();
          onPositionChange(smallContainerSlide);
        }
      }, 20);

      node.addEventListener('scroll', handleScrollPosition, { passive: true });

      return () => {
        node.removeEventListener('scroll', handleScrollPosition);
      };
    }, [controls, currentSlidesInView, onPositionChange]);

    return (
      <Styled.CarouselContainer {...rest}>
        <Styled.Carousel
          ref={CarouselRef}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={resetDragState}
          onMouseLeave={resetDragState}
          onMouseEnter={() => setHasSnappingRule(true)}
          onTouchStart={() => setHasSnappingRule(true)}
          isDragging={onDrag.isDragging}
          hasSnappingRule={hasSnappingRule}
          $styleQueryMode={styleQueryMode}
        >
          {children.map((child, i) => (
            <Styled.Content
              numSlides={numSlides}
              tabIndex={0}
              key={i}
              withPeeking={withPeeking}
              withPadding={withPadding}
              withMargin={withMargin}
              $styleQueryMode={styleQueryMode}
            >
              <InView
                parent={CarouselRef.current}
                setElementInView={handleInViewChange}
                position={i}
                isLazy={false}
              >
                {child}
              </InView>
            </Styled.Content>
          ))}
        </Styled.Carousel>
        {carouselRefHasLoaded &&
          controls === 'native' &&
          React.Children.count(children) > visibleSlidesCount && (
            <>
              {!currentSlidesInView.includes(0) && (
                <Styled.CarouselDirection
                  aria-label="previous"
                  direction="previous"
                  onClick={(e) => onSlideChange(CarouselRef.current, e)}
                  showDots={showDots}
                  $withArrowButtonVerticalOffset={withArrowButtonVerticalOffset}
                  $styleQueryMode={styleQueryMode}
                >
                  <IcnChevron
                    style={{
                      transform: 'rotate(180deg)',
                      width: '10px',
                      height: '10px'
                    }}
                  />
                </Styled.CarouselDirection>
              )}
              {!currentSlidesInView.includes(
                React.Children.count(children) - 1
              ) && (
                <Styled.CarouselDirection
                  aria-label="next"
                  direction="next"
                  onClick={(e) => onSlideChange(CarouselRef.current, e)}
                  showDots={showDots}
                  $withArrowButtonVerticalOffset={withArrowButtonVerticalOffset}
                >
                  <IcnChevron style={{ width: '10px', height: '10px' }} />
                </Styled.CarouselDirection>
              )}
            </>
          )}
        {carouselRefHasLoaded &&
          showDots &&
          controls === 'native' &&
          React.Children.count(children) > currentSlidesInView.length && (
            <Styled.CarouselDots
              count={React.Children.count(children)}
              selectedIndex={currentSlidesInView[0]}
            />
          )}
      </Styled.CarouselContainer>
    );
  }
);

Carousel.propTypes = {
  /**
   * Carousel slides - any ReactElement
   */
  children: PropTypes.arrayOf(PropTypes.element).isRequired,
  /**
   * Number of slides expected to show at a time.
   * This determines whether to show the next/previous arrows.
   */
  visibleSlidesCount: PropTypes.number,
  /**
   * The amount of slides to show on small, medium, and large viewports
   * The carousel expects a value for small and will default to small when a value for medium or large is not passed
   */
  numSlides: PropTypes.shape({
    small: PropTypes.number.isRequired,
    medium: PropTypes.number,
    large: PropTypes.number
  }),
  /**
   * `true` to display pagination dots
   */
  showDots: PropTypes.bool,
  /**
   * `true` to show a slide and a portion of the next slide
   */
  withPeeking: PropTypes.bool,
  /**
   * `true` to show padding on the left and right of the Carousel
   */
  withPadding: PropTypes.bool,
  /**
   * `true` to show margin between the slides
   */
  withMargin: PropTypes.bool,
  /**
   * Adjusts the vertical alignment of the button when there isn't a title at the bottom of a slide
   *
   * By default the carousel assumes that there is a title present at the bottom of a card
   *
   * @default true
   */
  withArrowButtonVerticalOffset: PropTypes.bool,
  /**
   * Controls mode - 'native' shows built-in arrows/dots, 'external' uses external controls
   */
  controls: PropTypes.oneOf(['native', 'external']),
  /**
   * Callback function to be called when the position of the carousel changes
   * @param {number} slideNumber - Current slide number (1-based index)
   */
  onPositionChange: PropTypes.func,
  /**
   * When true, the carousel will be in a small container
   */
  isInSmallContainer: PropTypes.bool
};

Carousel.defaultProps = {
  visibleSlidesCount: 3,
  numSlides: { small: 1, medium: 1, large: 1 },
  showDots: false,
  withPeeking: true,
  withPadding: true,
  withMargin: true,
  withArrowButtonVerticalOffset: true,
  controls: 'native',
  onPositionChange: () => {},
  isInSmallContainer: false
};

export default Carousel;
